/* eslint-disable no-unsafe-finally */
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";

import debug from "src/modules/debug-logger";
import { useApp } from "src/components/App/Context";
import { RPCRequests, Message } from "src/modules/communication-protobuf";

import { getDefaultLullabyId, getLullabies } from "./duck/selectors";
import { setDefaultLullaby } from "./duck/actions";
import time from "src/utils/time";

export const Status = {
  RESTING: "RESTING",
  LOADING: "LOADING",
  PLAYING: "PLAYING",
  STOPPING: "STOPPING"
};

export default function useParentStationLullabyPlayer() {
  const defaultLullabyId = useSelector(getDefaultLullabyId);
  const app = useApp();
  const store = useStore();
  const dispatch = useDispatch();
  const lullabies = useSelector(getLullabies);

  const monitoringIdRef = useRef();
  const babyJidRef = useRef();
  const messengerRef = useRef();
  const rpcRef = useRef();

  const stateRef = useRef({
    isPlaying: false,
    currentTime: 0,
    settings: {
      serverId: defaultLullabyId,
      volume: 0.5,
      timerSettings: {
        enabled: false,
        duration: 0
      }
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [status, setStatus] = useState(Status.RESTING);
  const [hasError, setHasError] = useState(false);
  const [startPlayDate, setStartPlayDate] = useState();
  const [volume, setVolume] = useState();
  const [timerSettings, setTimerSettings] = useState({});

  const getMissingLullaby = useCallback(
    async (missingLullabyId) => {
      try {
        await app.synchronizer.synchronizeLullabies();
      } catch (error) {
        debug.lullaby("getMissingLullaby > FAILED", error);
      } finally {
        if (getLullabies(store.getState())[missingLullabyId] == null) {
          debug.lullaby("Can't resolve required lullaby.");
          return false;
        }
        return true;
      }
    },
    [app.synchronizer, store]
  );

  const stateUpdateHandler = useCallback(
    async (update) => {
      const previousState = stateRef.current;
      const newState = { ...previousState, ...update };

      if (lullabies[newState.settings.serverId] == null) {
        const didSucceed = await getMissingLullaby(newState.settings.serverId);
        if (!didSucceed) {
          return;
        }
      }

      // we probably received new state before synchronization finished
      if (stateRef.current !== previousState) return;
      stateRef.current = newState;

      const { serverId } = newState.settings;
      if (getDefaultLullabyId(store.getState()) !== serverId) {
        dispatch(setDefaultLullaby(serverId));
      }

      const isDifferentLullaby =
        newState.settings.serverId !== previousState.settings.serverId;
      if (
        newState.isPlaying &&
        (!previousState.isPlaying || isDifferentLullaby)
      ) {
        setStartPlayDate(time(Date.now() - newState.currentTime));
        setStatus(Status.PLAYING);
      } else if (!newState.isPlaying && previousState.isPlaying) {
        setStartPlayDate(null);
        setStatus(Status.RESTING);
      }
      setVolume(newState.settings.volume);
      const { enabled, duration } = newState.settings.timerSettings;
      setTimerSettings({
        enabled,
        duration
      });
    },
    [lullabies, dispatch, getMissingLullaby, store]
  );

  const initialize = useCallback(
    async ({ initialState, monitoringId, babyJid, messenger, rpc }) => {
      monitoringIdRef.current = monitoringId;
      babyJidRef.current = babyJid;
      messengerRef.current = messenger;
      rpcRef.current = rpc;
      await stateUpdateHandler(initialState);
      setIsInitialized(true);
    },
    [stateUpdateHandler]
  );

  useEffect(() => {
    if (!isInitialized) return;
    const messenger = messengerRef.current;
    function lullabyPlayerStateMessageHandler({ state }, from) {
      if (from !== babyJidRef.current) return;
      stateUpdateHandler(state);
    }
    messenger.on(
      Message.LullabyPlayerStateMessage.name,
      lullabyPlayerStateMessageHandler
    );

    return () => {
      messenger.off(
        Message.LullabyPlayerStateMessage.name,
        lullabyPlayerStateMessageHandler
      );
    };
  }, [isInitialized, stateUpdateHandler]);

  const stop = useCallback(async () => {
    setStatus(Status.STOPPING);
    try {
      await rpcRef.current.call(
        RPCRequests.StopLullaby.create(),
        babyJidRef.current
      );
      stateUpdateHandler({
        ...stateRef.current,
        isPlaying: false,
        currentTime: 0
      });
    } catch (error) {
      debug.lullaby("StopLullabyRpcRequest FAILED", error);
      setStatus(Status.RESTING);
      setHasError(true);
    }
  }, [stateUpdateHandler]);

  const play = useCallback(async () => {
    setStatus(Status.LOADING);

    const requestedLullabyId = stateRef.current.settings.serverId;
    const request = RPCRequests.PlayLullaby.create(stateRef.current.settings);
    try {
      const { result } = await rpcRef.current.call(request, babyJidRef.current);
      if (result === "NOT_AVAILABLE") {
        setHasError(true);
        setStatus(Status.RESTING);
        return;
      } else if (result === "PREPARING") {
        // we received LullabPlayerStateMessage before PlayLullabyRpcResponse
        const { isPlaying, settings } = stateRef.current;
        if (isPlaying && settings.serverId === requestedLullabyId) {
          return;
        }
        setStatus(Status.LOADING);
      }
    } catch (error) {
      setHasError(true);
      setStatus(Status.RESTING);
    }
  }, []);

  const select = useCallback(
    async (lullabyId) => {
      if (status === Status.LOADING || status === Status.STOPPING) return;
      const wasPlaying = status === Status.PLAYING;

      stateUpdateHandler({
        ...stateRef.current,
        isPlaying: false,
        currentTime: 0,
        settings: { ...stateRef.current.settings, serverId: lullabyId }
      });

      if (wasPlaying) {
        play();
      }
    },
    [stateUpdateHandler, status, play]
  );

  const updateTimerSettings = useCallback(
    async (timerSettings) => {
      if (!stateRef.current.isPlaying) {
        await stateUpdateHandler({
          settings: { ...stateRef.current.settings, timerSettings }
        });
        return;
      }
      await rpcRef.current.call(
        RPCRequests.SetLullabyPlayerTimer.create(timerSettings),
        babyJidRef.current
      );
    },
    [stateUpdateHandler]
  );

  const getCurrentTime = useCallback(
    () => (stateRef.current.isPlaying ? time().diff(startPlayDate) : 0),
    [startPlayDate]
  );
  const getLullaby = useCallback(
    (lullabyId = defaultLullabyId) => lullabies[lullabyId],
    [defaultLullabyId, lullabies]
  );

  const clearError = useCallback(() => setHasError(false), []);

  return useMemo(
    () => ({
      stateUpdateHandler,
      initialize,
      play,
      stop,
      select,
      status,
      getCurrentTime,
      getLullaby,
      volume,
      timerSettings,
      updateTimerSettings,
      Status,
      hasError,
      clearError,
      stationType: "parent"
    }),
    [
      stateUpdateHandler,
      initialize,
      select,
      status,
      hasError,
      clearError,
      stop,
      play,
      getCurrentTime,
      getLullaby,
      volume,
      timerSettings,
      updateTimerSettings
    ]
  );
}
